﻿import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { useFormSubmit } from "../../../hooks/useFormSubmit";
import FormSkeleton from "../../../components/FormSkeleton";

export default function QuoteFormSection() {
  const [agreed, setAgreed] = useState(false);
  const [ready, setReady] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = useFormSubmit(agreed);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="presupuesto" className="bg-surface-alt py-16 md:py-24">
      <div className="max-w-container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          {/* Left: 40% */}
          <div className="w-full lg:w-[40%]">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading font-extrabold text-3xl md:text-[40px] text-text-primary mb-4 leading-tight">
                Pídenos presupuesto gratis
              </h2>
              <p className="font-body text-lg text-text-secondary mb-8">
                Te respondemos en 24h sin compromiso.
              </p>

              <div className="flex flex-col gap-4">
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors w-fit"
                >
                  <span>📞</span> 951 12 07 70
                </a>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors w-fit"
                >
                  <i className="ri-whatsapp-line text-xl"></i> WhatsApp 627 21 33 47
                </a>
              </div>
            </motion.div>
          </div>

          {/* Right: 60% */}
          <motion.div
            className="w-full lg:w-[60%]"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm min-h-[600px]">
              {!ready ? (
                <FormSkeleton />
              ) : (
                <form
                  ref={formRef}
                  action="https://formspree.io/f/mdajpjzd"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  <div>
                    <label htmlFor="nombre" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Nombre <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="telefono" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Teléfono <span className="text-primary">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 border border-r-0 border-border rounded-l-xl bg-slate-50 font-body text-sm text-text-secondary shrink-0">
                        +34
                      </span>
                      <input
                        id="telefono"
                        name="telefono"
                        type="tel"
                        required
                        className="w-full h-12 px-4 border border-border rounded-r-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="612 34 56 78"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="ciudad" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Ciudad <span className="text-primary">*</span>
                      </label>
                      <select
                        id="ciudad"
                        name="ciudad"
                        required
                        className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                      >
                        <option value="">Selecciona...</option>
                        <option value="Málaga">Málaga</option>
                        <option value="Marbella">Marbella</option>
                        <option value="Fuengirola">Fuengirola</option>
                        <option value="Torremolinos">Torremolinos</option>
                        <option value="Benalmádena">Benalmádena</option>
                        <option value="Mijas">Mijas</option>
                        <option value="Estepona">Estepona</option>
                        <option value="Otra">Otra</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="servicio" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Servicio <span className="text-primary">*</span>
                      </label>
                      <select
                        id="servicio"
                        name="servicio"
                        required
                        className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                      >
                        <option value="">Selecciona...</option>
                        <option value="Persianas">Persianas</option>
                        <option value="Toldos">Toldos</option>
                        <option value="Mosquiteras">Mosquiteras</option>
                        <option value="Otros">Otros</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="urgencia" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      ¿Cuándo lo necesitas? <span className="text-primary">*</span>
                    </label>
                    <select
                      id="urgencia"
                      name="urgencia"
                      required
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                    >
                      <option value="">Selecciona...</option>
                      <option value="Urgente (hoy/mañana)">Urgente (hoy/mañana)</option>
                      <option value="Esta semana">Esta semana</option>
                      <option value="Sin prisa (planificado)">Sin prisa (planificado)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mensaje" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Mensaje <span className="text-text-secondary">(opcional)</span>
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                      placeholder="Cuéntanos qué necesitas..."
                    ></textarea>
                    <p className="mt-1 text-xs text-text-secondary font-body">Máximo 500 caracteres</p>
                  </div>

                  <div className="flex items-start gap-2.5">
                    <input
                      id="privacidad"
                      name="privacidad"
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                    />
                    <div>
                      <label htmlFor="privacidad" className="font-body text-sm text-text-secondary leading-relaxed">
                        He leído y acepto la{" "}
                        <a href="/politica-privacidad" className="underline text-secondary hover:text-secondary-dark transition-colors">
                          Política de Privacidad
                        </a>{" "}
                        <span className="text-primary">*</span>
                      </label>
                      <p className="font-body text-xs text-text-secondary/70 mt-1 leading-relaxed">
                        Responsable: Persianas Toldos Málaga. Finalidad: responder a tu consulta y enviarte presupuesto. Legitimación: tu consentimiento. Destinatarios: no se cederán datos a terceros. Derechos: puedes ejercer tus derechos de acceso, rectificación y supresión escribiendo a info@persianas-toldos-malaga.es
                      </p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full h-14 bg-text-primary hover:bg-slate-800 text-white font-heading font-bold text-lg rounded-pill transition-colors flex items-center justify-center gap-2"
                  >
                    📩 Recibir presupuesto en 24h <span>→</span>
                  </button>

                  <p className="text-center font-body text-xs text-text-secondary mt-1">
                    Sin compromiso · Sin spam · Respuesta garantizada
                  </p>

                  <p className="text-center font-body text-sm text-text-secondary">
                    o llámanos al{" "}
                    <a href="tel:+34951120770" className="font-semibold text-primary hover:text-primary-dark underline">
                      951 12 07 70
                    </a>{" "}
                    |{" "}
                    <a
                      href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-whatsapp hover:text-[#128C7E] underline"
                    >
                      WhatsApp 627 21 33 47
                    </a>
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}